/**
 * AlphaAlertsPanel — pending trade signals with APPROVE / REJECT buttons.
 * Falls back to recent agent_signal events when no pending signals exist.
 */

import {
  useAgentEvents,
  usePendingSignals,
  useApproveSignal,
  useRejectSignal,
} from "@/sdk/hooks/use-agents";
import { parsePayload } from "@/sdk/types/agents";
import type { AgentEvent, PendingSignal } from "@/sdk/types/agents";

function SignalCard({ signal }: { signal: PendingSignal }) {
  const approve = useApproveSignal();
  const reject = useRejectSignal();

  // A failed APPROVE/REJECT is a trade-authorization that did NOT land — it
  // must be visible. The ApiError message carries the backend detail
  // ("Not authenticated", "already resolved", ...), so render it verbatim.
  const actionError = approve.isError ? approve.error : reject.isError ? reject.error : null;
  const actionLabel = approve.isError ? "Approve failed" : "Reject failed";

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <span className={`text-xs font-bold uppercase ${signal.side === "BUY" ? "text-emerald-400" : "text-red-400"}`}>
          {signal.side}
        </span>
        <span className="text-[10px] text-zinc-500">
          {new Date(signal.created_at).toLocaleTimeString()}
        </span>
      </div>

      <p className="mb-2 text-xs leading-snug text-zinc-300 line-clamp-2">
        {signal.question}
      </p>

      <div className="mb-2 grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-zinc-500">Size</div>
          <div className="font-mono text-zinc-200">${(signal.size ?? 0).toFixed(0)}</div>
        </div>
        <div>
          <div className="text-zinc-500">Conf</div>
          <div className="font-mono text-zinc-200">{((signal.confidence ?? 0) * 100).toFixed(0)}%</div>
        </div>
        <div>
          <div className="text-zinc-500">Edge</div>
          <div className={`font-mono ${(signal.edge ?? 0) > 0 ? "text-emerald-400" : "text-red-400"}`}>
            {(signal.edge ?? 0) > 0 ? "+" : ""}{((signal.edge ?? 0) * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      {signal.reasoning && (
        <p className="mb-2 text-[10px] leading-snug text-zinc-500 line-clamp-2">
          {signal.reasoning}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => approve.mutate(signal.id)}
          disabled={approve.isPending}
          className="flex-1 rounded bg-emerald-600 py-1.5 text-xs font-bold text-white hover:bg-emerald-500 disabled:opacity-50"
        >
          {approve.isPending ? "..." : "APPROVE"}
        </button>
        <button
          type="button"
          onClick={() => reject.mutate(signal.id)}
          disabled={reject.isPending}
          className="flex-1 rounded bg-zinc-700 py-1.5 text-xs font-bold text-zinc-300 hover:bg-zinc-600 disabled:opacity-50"
        >
          {reject.isPending ? "..." : "REJECT"}
        </button>
      </div>

      {actionError != null && (
        <p role="alert" className="mt-1.5 text-[10px] font-semibold text-red-400">
          {actionLabel}
          {actionError instanceof Error && actionError.message ? `: ${actionError.message}` : ""}
        </p>
      )}
    </div>
  );
}

function EventCard({ event }: { event: AgentEvent }) {
  // payload arrives as a JSON string (asyncpg JSONB-as-text) or null.
  const payload = parsePayload(event.payload);
  const side = typeof payload.side === "string" ? payload.side : "";
  const confidence = typeof payload.confidence === "number" ? payload.confidence : undefined;
  const edge = typeof payload.edge === "number" ? payload.edge : undefined;

  return (
    <div className="rounded-lg border border-zinc-800/50 bg-zinc-900/30 p-2.5">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {side && (
            <span className={`text-[10px] font-bold uppercase ${side === "BUY" ? "text-emerald-400" : "text-red-400"}`}>
              {side}
            </span>
          )}
          <span className="text-[10px] text-zinc-500">{event.agent_id}</span>
        </div>
        <span className="text-[10px] text-zinc-600">
          {new Date(event.created_at).toLocaleTimeString()}
        </span>
      </div>
      <p className="text-xs text-zinc-400 line-clamp-1">
        {typeof payload.question === "string" ? payload.question : event.event_type}
      </p>
      {(confidence != null || edge != null) && (
        <div className="mt-1 flex gap-3 text-[10px]">
          {confidence != null && (
            <span className="text-zinc-500">
              Conf: <span className="text-zinc-300">{(confidence * 100).toFixed(0)}%</span>
            </span>
          )}
          {edge != null && (
            <span className="text-zinc-500">
              Edge: <span className={edge > 0 ? "text-emerald-400" : "text-red-400"}>
                {edge > 0 ? "+" : ""}{(edge * 100).toFixed(1)}%
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function AlphaAlertsPanel() {
  const {
    data: pending,
    isLoading: loadingPending,
    isError: pendingError,
  } = usePendingSignals();
  const { data: events, isError: eventsError } = useAgentEvents(20);

  const signals = pending?.signals ?? [];
  const signalEvents = (events?.events ?? []).filter(
    (e) => e.event_type === "agent_signal",
  );

  if (loadingPending) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-500">
        Loading signals...
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-zinc-950">
      {/* Pending signals (approval mode). A failed read must never look like
          "nothing pending" — approvals could exist and silently expire. */}
      {pendingError ? (
        <div className="border-b border-zinc-800 p-2">
          <p role="alert" className="text-xs font-semibold text-amber-400">
            Pending signals unavailable — backend unreachable
          </p>
        </div>
      ) : (
        signals.length > 0 && (
          <div className="border-b border-zinc-800 p-2">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-bold text-amber-400">PENDING APPROVAL</span>
              <span className="rounded-full bg-amber-950 px-1.5 text-[10px] font-bold text-amber-400">
                {signals.length}
              </span>
            </div>
            <div className="space-y-2">
              {signals.map((s) => (
                <SignalCard key={s.id} signal={s} />
              ))}
            </div>
          </div>
        )
      )}

      {/* Recent signal events */}
      <div className="flex-1 overflow-auto p-2">
        <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
          Recent Signals
        </div>
        {signalEvents.length === 0 ? (
          eventsError ? (
            <div className="mt-8 text-center text-xs text-amber-400">
              Signal feed unavailable — backend unreachable
            </div>
          ) : (
            <div className="mt-8 text-center text-xs text-zinc-600">
              No signals yet
            </div>
          )
        ) : (
          <div className="space-y-1.5">
            {signalEvents.map((e) => (
              <EventCard key={e.id} event={e} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
